import React, { useEffect, useState } from "react";
import { Plus, Trash2, X, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmModal";
import { useAuth } from "@/context/AuthContext";
import HelpHint from "@/components/HelpHint";
import SlotsEditor from "@/components/SlotsEditor";
import { DEFAULT_SLOTS, SLOT_KEYS, SLOT_LABELS } from "@/lib/slots";

function ChangePasswordModal({ onClose }) {
  const [curr, setCurr] = useState("");
  const [nw, setNw] = useState("");
  const [conf, setConf] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!curr || !nw || !conf) return toast.error("Tous les champs requis");
    if (nw.length < 8) return toast.error("Min. 8 caractères");
    if (nw !== conf) return toast.error("Les nouveaux mots de passe ne correspondent pas");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: curr, new_password: nw });
      toast.success("Mot de passe changé");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[var(--surface)] border border-[var(--border-strong)] rounded-2xl p-6 max-w-md w-full shadow-2xl" data-testid="change-password-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="label">Sécurité</div>
            <h3 className="font-serif text-3xl">Changer mot de passe</h3>
          </div>
          <button onClick={onClose} className="text-[var(--muted)] hover:text-black"><X size={20} /></button>
        </div>
        <div className="space-y-3 mb-5">
          <Field label="Mot de passe actuel">
            <input type="password" value={curr} onChange={(e) => setCurr(e.target.value)} className="input-box" data-testid="current-password-input" />
          </Field>
          <Field label="Nouveau mot de passe">
            <input type="password" value={nw} onChange={(e) => setNw(e.target.value)} className="input-box" data-testid="new-password-input" />
          </Field>
          <Field label="Confirmer">
            <input type="password" value={conf} onChange={(e) => setConf(e.target.value)} className="input-box" data-testid="confirm-password-input" />
          </Field>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Annuler</button>
          <button onClick={submit} disabled={loading} className="btn-primary" data-testid="save-password-btn">
            {loading ? "Enregistrement…" : "Changer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

export default function SettingsPage() {
  const { user, refresh } = useAuth();
  const confirm = useConfirm();
  const [grades, setGrades] = useState([]);
  const [settings, setSettings] = useState({ business_name: "", packages: {} });
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [g, s] = await Promise.all([api.get("/grades"), api.get("/settings")]);
        setGrades(g.data.grades);
        setSettings(s.data.settings);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    })();
  }, []);

  const updateGrade = async (id, patch) => {
    const g = grades.find((x) => x.id === id);
    const next = { ...g, ...patch };
    setGrades(grades.map((x) => (x.id === id ? next : x)));
    try {
      await api.put(`/grades/${id}`, { name: next.name, type: next.type, rate: Number(next.rate) || 0 });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addGrade = async (type) => {
    try {
      const { data } = await api.post("/grades", { name: "Nouveau grade", type, rate: 0 });
      setGrades([...grades, data.grade]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const removeGrade = async (g) => {
    const ok = await confirm.ask({
      title: "Supprimer ce grade?",
      message: `"${g.name}" sera retiré. Les officiels qui l'avaient ne pourront plus être payés selon ce grade.`,
      confirmLabel: "Supprimer",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/grades/${g.id}`);
      setGrades(grades.filter((x) => x.id !== g.id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      await api.put("/settings", patch);
      if (patch.business_name !== undefined) await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updatePackage = (key, patch) => {
    const packages = { ...settings.packages, [key]: { ...settings.packages[key], ...patch } };
    saveSettings({ packages });
  };

  const ref = grades.filter((g) => g.type === "referee");
  const sc = grades.filter((g) => g.type === "scorer");

  return (
    <div data-testid="settings-page">
      {confirm.modal}
      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}

      <div className="mb-10">
        <div className="section-number">04 / Réglages</div>
        <h1 className="h1-big">Ton terrain.</h1>
        <p className="text-sm text-[var(--muted)] mt-3 max-w-xl leading-relaxed">
          Configure grades, tarifs et infos de facturation.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Business info */}
        <div className="card-flat">
          <div className="label mb-3">Infos entreprise</div>
          <Field label="Nom apparaissant sur les PDF">
            <input
              value={settings.business_name || ""}
              onChange={(e) => setSettings({ ...settings, business_name: e.target.value })}
              onBlur={(e) => saveSettings({ business_name: e.target.value })}
              className="input-box"
              data-testid="business-name-input"
            />
          </Field>
          <Field label="Téléphone sur la facture (optionnel)">
            <input
              value={settings.business_phone || ""}
              onChange={(e) => setSettings({ ...settings, business_phone: e.target.value })}
              onBlur={(e) => saveSettings({ business_phone: e.target.value })}
              className="input-box"
              placeholder="873-344-4777"
              data-testid="business-phone-input"
            />
          </Field>
        </div>

        {/* Security */}
        <div className="card-flat">
          <div className="label mb-3">Sécurité</div>
          <p className="text-xs text-[var(--muted)] mb-4 leading-relaxed">
            Ton mot de passe protège l'accès à toutes tes données. Change-le si tu le partages par erreur.
          </p>
          <span className="inline-flex items-center">
            <button onClick={() => setShowPwd(true)} className="btn-secondary" data-testid="change-password-btn">
              <Lock size={14} strokeWidth={2.5} /> Changer mot de passe
            </button>
            <HelpHint
              title="Changer mot de passe"
              text="Modifie ton mot de passe administrateur. Minimum 8 caractères. Tu devras saisir ton mot de passe actuel pour confirmer. Toutes tes sessions actives restent valides après le changement."
              testid="hint-change-password"
            />
          </span>
        </div>

        {/* Referee grades */}
        <GradeList title="Grades arbitres" sub="Tarif payé à l'arbitre par partie." grades={ref} type="referee" onAdd={addGrade} onUpdate={updateGrade} onRemove={removeGrade} testid="ref-grade" />

        {/* Scorer grades */}
        <GradeList title="Grades marqueurs" sub="Tarif payé au marqueur par partie." grades={sc} type="scorer" onAdd={addGrade} onUpdate={updateGrade} onRemove={removeGrade} testid="sc-grade" />

        {/* Availability slots (AM / PM / SOIRÉE) */}
        <div className="card-flat lg:col-span-2">
          <div className="flex items-center gap-1 mb-1">
            <div className="label !mb-0">Créneaux de disponibilités (AM / PM / SOIRÉE)</div>
            <HelpHint
              title="Créneaux AM/PM/SOIRÉE"
              text="Ces heures servent de défaut pour TOUS tes événements. Chaque officiel répondra Disponible/Indispo par créneau (au lieu d'une réponse unique par date). Tu peux personnaliser ces heures événement par événement dans l'onglet 'Disponibilités' de chaque événement. Une partie qui commence à 13:30 sera assignée au créneau PM (12:00-17:00 par défaut)."
              testid="hint-slots"
              placement="bottom"
              align="start"
            />
          </div>
          <p className="text-xs text-[var(--muted)] mb-4 leading-relaxed">
            Configure les heures par défaut. Override possible par événement (ex: tournoi de soir uniquement).
          </p>
          <SlotsEditor
            slots={settings.availability_slots || DEFAULT_SLOTS}
            onSave={(slots) => saveSettings({ availability_slots: slots })}
            testidPrefix="settings-slot"
          />
        </div>

        {/* Packages */}
        <div className="card-flat lg:col-span-2">
          <div className="flex items-center gap-1 mb-1">
            <div className="label !mb-0">Forfaits de facturation client</div>
            <HelpHint
              title="Forfaits A / B / C / D"
              text="Le système détecte AUTOMATIQUEMENT le forfait à facturer pour chaque partie selon les officiels assignés : A = 1 arbitre seul · B = 2 arbitres · C = 1 arbitre + 1 marqueur · D = 2 arbitres + 1 marqueur. Le montant facturé au client = forfait détecté × nombre de parties (séparé du tarif payé aux officiels)."
              testid="hint-packages"
              placement="bottom"
              align="start"
            />
          </div>
          <p className="text-xs text-[var(--muted)] mb-4 leading-relaxed">
            L'app détecte automatiquement le forfait à facturer pour chaque partie selon la composition d'officiels assignée.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: "A", composition: "1 arbitre seul" },
              { key: "B", composition: "2 arbitres" },
              { key: "C", composition: "1 arbitre + 1 marqueur" },
              { key: "D", composition: "2 arbitres + 1 marqueur" },
            ].map(({ key, composition }) => {
              const pkg = (settings.packages || {})[key] || { label: composition, rate: 0 };
              return (
                <div key={key} className="border border-black p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-black text-white flex items-center justify-center font-serif text-2xl">{key}</div>
                    <div>
                      <div className="font-bold text-sm">Forfait {key}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">{composition}</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Field label="Libellé">
                      <input
                        value={pkg.label || ""}
                        onChange={(e) => setSettings({ ...settings, packages: { ...settings.packages, [key]: { ...pkg, label: e.target.value } } })}
                        onBlur={(e) => updatePackage(key, { label: e.target.value })}
                        className="input-box"
                        data-testid={`pkg-${key}-label-input`}
                      />
                    </Field>
                    <Field label="Tarif ($/partie)">
                      <input
                        type="number"
                        step="0.01"
                        value={pkg.rate ?? 0}
                        onChange={(e) => setSettings({ ...settings, packages: { ...settings.packages, [key]: { ...pkg, rate: parseFloat(e.target.value) || 0 } } })}
                        onBlur={(e) => updatePackage(key, { rate: parseFloat(e.target.value) || 0 })}
                        className="input-box"
                        data-testid={`pkg-${key}-rate-input`}
                      />
                    </Field>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function GradeList({ title, sub, grades, type, onAdd, onUpdate, onRemove, testid }) {
  const helpText =
    type === "referee"
      ? "Configure les 5 grades d'arbitre (1 à 5) avec leur tarif payé par partie. Ces grades apparaissent dans la fiche de chaque officiel et déterminent le picker d'horaire. Le tarif est multiplié par le nombre de parties pour la paie de base."
      : "Configure les grades de marqueur avec leur tarif payé par partie. Distinct du grade arbitre — un officiel peut avoir les deux. La paie est calculée séparément selon le rôle assigné à chaque partie.";
  return (
    <div className="card-flat">
      <div className="flex items-center gap-1 mb-1">
        <div className="label !mb-0">{title}</div>
        <HelpHint title={title} text={helpText} testid={`hint-${testid}`} placement="bottom" align="start" />
      </div>
      <p className="text-xs text-[var(--muted)] mb-4">{sub}</p>
      <div className="space-y-2">
        {grades.map((g) => (
          <div key={g.id} className="flex items-center gap-2 border-b border-[var(--border)] pb-2" data-testid={`${testid}-row-${g.id}`}>
            <input
              value={g.name}
              onChange={(e) => onUpdate(g.id, { name: e.target.value })}
              className="bg-transparent flex-1 font-mono text-sm outline-none border-b border-transparent focus:border-black"
            />
            <input
              type="number"
              step="0.01"
              value={g.rate}
              onChange={(e) => onUpdate(g.id, { rate: parseFloat(e.target.value) || 0 })}
              className="bg-white border border-black px-2 py-1 w-20 outline-none font-mono text-xs tabular"
            />
            <span className="text-xs text-[var(--muted)]">$</span>
            <button onClick={() => onRemove(g)} className="text-[var(--muted)] hover:text-black p-1">
              <Trash2 size={13} strokeWidth={2.5} />
            </button>
          </div>
        ))}
        <button
          onClick={() => onAdd(type)}
          className="w-full border-2 border-dashed border-[var(--border-strong)] p-2 text-[var(--muted)] hover:border-black hover:text-black text-xs uppercase tracking-widest flex items-center justify-center gap-1"
          data-testid={`add-${testid}-btn`}
        >
          <Plus size={12} strokeWidth={2.5} /> Ajouter un grade
        </button>
      </div>
    </div>
  );
}
